// src/features/text/word-counter/WordCounter.tsx
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, Typography, TextField, Paper, Grid, Divider, IconButton, Tooltip
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import StyledFAQ from '../../common/components/StyledFAQ';
import { getTextStats } from '../utils/textUtils';

interface StatDisplayProps {
    label: string;
    value: string | number;
}

const StatDisplay: React.FC<StatDisplayProps> = ({ label, value }) => (
    <Grid item xs={6} sm={4} sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        p: 2,
    }}>
        <Typography variant="h4" component="p" sx={{ fontWeight: 'bold' }}>
            {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
    </Grid>
);

const WordCounter: React.FC = () => {
    const { t } = useTranslation('tools');
    const [inputText, setInputText] = useState<string>('');

    const stats = useMemo(() => getTextStats(inputText), [inputText]);

    const faqs = [
        { q: 'text_word_counter_faq1_q', a: 'text_word_counter_faq1_a' },
        { q: 'text_word_counter_faq2_q', a: 'text_word_counter_faq2_a' },
        { q: 'text_word_counter_faq3_q', a: 'text_word_counter_faq3_a' },
    ];

    return (
        <Box>
            <Paper elevation={0} variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" component="h3" gutterBottom>
                        {t('text_common_input_label')}
                    </Typography>
                    <Tooltip title={t('text_common_clear_input')}>
                        <span>
                            <IconButton onClick={() => setInputText('')} size="small" disabled={!inputText}>
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
                <TextField
                    multiline
                    rows={12}
                    fullWidth
                    variant="outlined"
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    placeholder={t('text_common_input_placeholder')}
                    sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'action.hover' } }}
                />
            </Paper>

            <Paper elevation={0} variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mt: 3, borderRadius: 2 }}>
                <Typography variant="h6" component="h3" gutterBottom textAlign="center" sx={{ mb: 2 }}>
                    {t('text_word_counter_stats_title')}
                </Typography>
                <Grid container spacing={1} justifyContent="center">
                    <StatDisplay label={t('text_word_counter_stat_words')} value={stats.words} />
                    <StatDisplay label={t('text_word_counter_stat_chars')} value={stats.characters} />
                    <StatDisplay label={t('text_word_counter_stat_lines')} value={stats.lines} />
                    <StatDisplay label={t('text_word_counter_stat_sentences')} value={stats.sentences} />
                    <StatDisplay label={t('text_word_counter_stat_paragraphs')} value={stats.paragraphs} />
                    <StatDisplay label={t('text_word_counter_stat_reading_time')} value={`${stats.readingTime} ${t('text_word_counter_stat_minutes')}`} />
                </Grid>
            </Paper>

            <Box sx={{ my: 8 }}>
                <Typography variant="h4" component="h2" gutterBottom textAlign="center">
                    {t('text_word_counter_about_title')}
                </Typography>
                <Typography paragraph color="text.secondary" textAlign="center" maxWidth="md" mx="auto">
                    {t('text_word_counter_about_desc')}
                </Typography>
                <StyledFAQ
                    title={t('faq_title')}
                    faqs={faqs}
                    t={t}
                />
            </Box>
        </Box>
    );
};

export default WordCounter;